import { useEffect, useRef, useState } from "react";
import { scrollState, onPhaseChange } from "../state/scroll";

/**
 * Slim vertical progress rail along the right edge.
 *
 * Visible only during PLOT phase — INTRO and MAP have their own
 * dedicated scroll choreography (camera flythrough, map zoom) so a
 * "page progress" rail there would just be confusing.
 *
 * Computes progress from the live document scroll, not scrollState,
 * so it reflects what the user actually sees regardless of any
 * ScrollTrigger phase quirks.
 */
export default function ScrollProgress() {
  const [phase, setPhase] = useState(scrollState.phase);
  const [progress, setProgress] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => onPhaseChange(setPhase), []);

  useEffect(() => {
    if (phase !== "PLOT") {
      setProgress(0);
      return undefined;
    }

    let last = -1;
    const tick = () => {
      const sy = window.scrollY || document.documentElement.scrollTop || 0;
      const max = Math.max(
        1,
        document.documentElement.scrollHeight - window.innerHeight
      );
      const p = Math.max(0, Math.min(1, sy / max));
      if (Math.abs(p - last) > 0.001) {
        last = p;
        setProgress(p);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [phase]);

  if (phase !== "PLOT" || window.location.pathname !== "/") return null;

  return (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        top: "12vh",
        right: "14px",
        bottom: "12vh",
        width: "2px",
        zIndex: 50,
        pointerEvents: "none",
        background: "rgba(212, 164, 92, 0.1)",
        borderRadius: "2px",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: `${progress * 100}%`,
          background:
            "linear-gradient(180deg, #d4a45c 0%, #eccb92 50%, #d4a45c 100%)",
          boxShadow: "0 0 14px rgba(212, 164, 92,0.6), 0 0 4px rgba(212, 164, 92,0.9)",
          borderRadius: "2px",
          transition: "height 80ms linear",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: `calc(${progress * 100}% - 4px)`,
          left: "-3px",
          width: "8px",
          height: "8px",
          borderRadius: "50%",
          background: "#eccb92",
          boxShadow: "0 0 16px rgba(212, 164, 92,1), 0 0 6px rgba(212, 164, 92,0.8)",
          transition: "top 80ms linear",
        }}
      />
    </div>
  );
}
